import React, { useState, useEffect } from 'react';
import { Trash2, AlertTriangle, CheckCircle, Clock, Search, Package, User, DollarSign, Calendar } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const statusConfig = {
  active: { label: 'Active', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20', icon: Clock },
  cancelled: { label: 'Taken Down', color: 'text-green-400 bg-green-400/10 border-green-400/20', icon: Trash2 },
  expired: { label: 'Expired', color: 'text-gray-400 bg-gray-400/10 border-gray-400/20', icon: CheckCircle },
  sold: { label: 'Sold', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20', icon: CheckCircle },
  pending: { label: 'Pending', color: 'text-orange-400 bg-orange-400/10 border-orange-400/20', icon: Clock },
};

export default function TakedownActions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmId, setConfirmId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('marketplace_listings')?.select(`
          id,
          title,
          asking_price,
          status,
          condition,
          created_at,
          updated_at,
          view_count,
          offer_count,
          user_profiles!marketplace_listings_seller_id_fkey(full_name, email),
          collectibles!marketplace_listings_collectible_id_fkey(collectible_type, name)
        `)?.order('updated_at', { ascending: false })?.limit(50);

      if (!error) {
        setItems(data || []);
      }
    } catch (err) {
      console.error('Error loading listings:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = items?.filter(item => {
    const matchSearch = item?.title?.toLowerCase()?.includes(search?.toLowerCase()) ||
      item?.user_profiles?.full_name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || item?.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleTakedown = async (id) => {
    setActionLoading(id);
    try {
      const { error } = await supabase?.from('marketplace_listings')?.update({ status: 'cancelled', updated_at: new Date()?.toISOString() })?.eq('id', id);
      if (!error) {
        setItems(prev => prev?.map(i => i?.id === id ? { ...i, status: 'cancelled' } : i));
        setConfirmId(null);
      }
    } catch (err) {
      console.error('Takedown error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (id) => {
    setActionLoading(id);
    try {
      const { error } = await supabase?.from('marketplace_listings')?.update({ status: 'active', updated_at: new Date()?.toISOString() })?.eq('id', id);
      if (!error) {
        setItems(prev => prev?.map(i => i?.id === id ? { ...i, status: 'active' } : i));
      }
    } catch (err) {
      console.error('Restore error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const activeCount = items?.filter(i => i?.status === 'active')?.length;
  const cancelledCount = items?.filter(i => i?.status === 'cancelled')?.length;

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1,2,3]?.map(i => <div key={i} className="h-24 bg-gray-800 rounded-xl border border-gray-700" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Banner */}
      {activeCount > 0 && (
        <div className="flex items-center gap-3 bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
          <AlertTriangle size={16} className="text-orange-400 flex-shrink-0" />
          <span className="text-sm text-orange-300">
            {activeCount} active listing{activeCount !== 1 ? 's' : ''} available for review
            {cancelledCount > 0 ? `, ${cancelledCount} already taken down` : ''}
          </span>
        </div>
      )}
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search items or sellers..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e?.target?.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg text-sm text-white px-3 py-2.5 focus:outline-none focus:border-blue-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="cancelled">Taken Down</option>
          <option value="expired">Expired</option>
          <option value="sold">Sold</option>
        </select>
      </div>
      {/* Items */}
      <div className="space-y-3">
        {filtered?.map(item => {
          const StatusIcon = statusConfig?.[item?.status]?.icon || Clock;
          return (
            <div key={item?.id} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Package size={14} className="text-gray-400 flex-shrink-0" />
                    <span className="font-medium text-white text-sm truncate">{item?.title}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap mt-1">
                    <span className="flex items-center gap-1"><User size={10} /> {item?.user_profiles?.full_name || item?.user_profiles?.email || 'Unknown'}</span>
                    <span className="flex items-center gap-1"><DollarSign size={10} /> ${parseFloat(item?.asking_price || 0)?.toLocaleString()}</span>
                    <span className="capitalize">{item?.collectibles?.collectible_type?.replace(/-/g, ' ') || '—'}</span>
                    <span className="flex items-center gap-1"><Calendar size={10} /> {new Date(item.created_at)?.toLocaleDateString()}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">Views: {item?.view_count || 0} · Offers: {item?.offer_count || 0}</div>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${statusConfig?.[item?.status]?.color || 'text-gray-400 bg-gray-400/10 border-gray-400/20'}`}>
                  <StatusIcon size={10} />
                  {statusConfig?.[item?.status]?.label || item?.status}
                </span>
              </div>
              {/* Actions */}
              {item?.status === 'active' && (
                <div className="flex gap-2 mt-4 pt-3 border-t border-gray-700">
                  {confirmId === item?.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-300">Confirm takedown?</span>
                      <button
                        onClick={() => handleTakedown(item?.id)}
                        disabled={actionLoading === item?.id}
                        className="flex items-center gap-2 px-3 py-2 bg-red-600/30 border border-red-600/40 text-red-300 rounded-lg text-xs hover:bg-red-600/40 transition-colors disabled:opacity-50"
                      >
                        <Trash2 size={12} /> Yes, Take Down
                      </button>
                      <button
                        onClick={() => setConfirmId(null)}
                        className="px-3 py-2 bg-gray-700/50 border border-gray-600 text-gray-400 rounded-lg text-xs hover:bg-gray-700 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmId(item?.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-red-600/20 border border-red-600/30 text-red-400 rounded-lg text-xs hover:bg-red-600/30 transition-colors"
                    >
                      <Trash2 size={12} /> Take Down Listing
                    </button>
                  )}
                </div>
              )}
              {item?.status === 'cancelled' && (
                <div className="flex gap-2 mt-4 pt-3 border-t border-gray-700">
                  <button
                    onClick={() => handleRestore(item?.id)}
                    disabled={actionLoading === item?.id}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-lg text-xs hover:bg-green-600/30 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle size={12} /> Restore Listing
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {filtered?.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Trash2 size={32} className="mx-auto mb-3 opacity-30" />
            <p>No listings match your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}
